import { ReplyIcon } from 'morpheus-ui';
import { Button } from '../../../../atoms/Button';
import { Typography } from '../../../../molecules/Typography';
import { ICommentAPI } from '../../index';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useUserStore } from '../../../../../../modules/Auth/stores/authStore';
import { useMemo, useState } from 'react';
import InputDiv from '../inputDiv';

interface ICommentContent {
  data: ICommentAPI;
  onReply: () => void;
  onLike: () => void;
  onDelete: (data: any) => void;
  deleteLoading?: boolean;
  disableReply?: boolean;
  isModal?: boolean;
  onUpdate: (data: any) => void;
}

export default function CommentContent({
  data,
  onReply,
  onLike,
  onDelete,
  disableReply,
  isModal = false,
  deleteLoading = false,
  onUpdate,
}: ICommentContent) {
  const { user } = useUserStore();

  const [isEditing, setIsEditing] = useState(false);

  const shouldShowDeleteButton = useMemo(() => {
    const dataUserId = data?.user?.id || data?.user?._id;
    return user?.id === dataUserId;
  }, [user, data]);

  const shouldShowLikeButton = useMemo(() => {
    const dataUserId = data?.user?.id || data?.user?._id;
    return user?.id !== dataUserId;
  }, [user, data]);

  const shouldShowUpdateButton = useMemo(() => {
    const dataUserId = data?.user?.id || data?.user?._id;
    return user?.id === dataUserId;
  }, [user, data]);

  return (
    <div
      className={`flex w-full min-w-0 flex-col gap-lg overflow-hidden break-words rounded-md bg-card-color-default p-5 shadow-level-1 ${
        isModal ? 'bg-color-lighter' : 'bg-color-lightest'
      }`}
    >
      <Typography size='h6' type='title' weight='bold' className=''>
        {data?.user?.name}
      </Typography>
      {isEditing ? (
        <InputDiv
          text={data.text}
          onSubmit={(text: string) => {
            setIsEditing(false);
            onUpdate(text);
          }}
        />
      ) : (
        <Typography size='default' className='break-words whitespace-pre-wrap'>
          {data.text}
        </Typography>
      )}
      <div className='flex flex-row items-end justify-between'>
        <Typography size='small'>
          {formatDistanceToNow(new Date(data.createdAt), {
            addSuffix: true,
            locale: ptBR,
          })}
        </Typography>
        <div className='flex flex-row items-end gap-xxl'>
          {shouldShowLikeButton && (
            <div className='flex flex-row items-end gap-sm'>
              <Typography size='default'>
                {data.qtdLikes < 10 && '0'}
                {data.qtdLikes}
              </Typography>

              <Button
                onlyIcon
                LeftIcon='thumb_up'
                iconType='material'
                iconVariant='rounded'
                classNameIcon={`filled ${
                  data.isLiked
                    ? 'text-brand-color-secondary-pure'
                    : 'text-mono-color-medium'
                }`}
                size='regular'
                variant='text'
                className='filled p-0'
                onClick={() => {
                  onLike();
                }}
              />
            </div>
          )}

          {shouldShowDeleteButton && (
            <Button
              onlyIcon
              LeftIcon='delete'
              iconType='material'
              iconVariant='rounded'
              classNameIcon={`filled text-primary-color-pure`}
              size='regular'
              variant='text'
              className='filled p-0'
              onClick={() => {
                onDelete(data);
              }}
              disabled={deleteLoading}
              loading={deleteLoading}
            />
          )}
          {shouldShowUpdateButton && (
            <Button
              onlyIcon
              LeftIcon='edit'
              iconType='material'
              iconVariant='rounded'
              classNameIcon={`filled text-primary-color-pure`}
              size='regular'
              variant='text'
              className='filled p-0'
              onClick={() => {
                setIsEditing((prev) => !prev);
              }}
            />
          )}
          {!disableReply && (
            <Button
              onlyIcon
              LeftIcon={ReplyIcon}
              iconType='svg'
              iconVariant='rounded'
              classNameIcon={`h-4 w-4 fill-button-color-tertiary-default`}
              size='small'
              variant='text'
              className='p-0'
              onClick={() => onReply()}
            />
          )}
        </div>
      </div>
    </div>
  );
}
